import {Injectable} from '@angular/core';
import {collection, Firestore, getDocs, query, QuerySnapshot, where} from '@angular/fire/firestore';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import {toRawStockCode} from '../../../shared/shared-utils/firestore/firestore.utils';
import {sItemKeyToInt} from '../../../shared-utilities/models-old/datastructures';
import {
  IStockManagerBarcodeAvailability,
  IStockManagerBarcodeStoreConflict,
} from '../models/stock-manager-barcode-availability.model';
import {chunkBarcodes, normalizeBarcodeValue, normalizeMultipleBarcodesValue} from '../utils/stock-manager-barcode.utils';
import {TStockCodeCheckTargets} from '../models/stock-manager-stock-code-availability.model';

@Injectable({
  providedIn: 'root',
})
export class StockManagerBarcodeAvailabilityService {

  constructor(private readonly firestore: Firestore) {
  }

  /**
   * Checks the given barcodes against the primary and additional barcodes of every stock item in
   * each target store. Used to stop a new item being created with a barcode that already exists.
   */
  async checkBarcodeAvailability(
    stores: TStockCodeCheckTargets,
    barcodes: string[],
  ): Promise<IStockManagerBarcodeAvailability> {
    const normalizedBarcodes = Array.from(new Set(
      barcodes.map((barcode: string) => normalizeBarcodeValue(barcode)).filter((barcode: string) => barcode.length > 0),
    ));

    if (normalizedBarcodes.length === 0 || stores.length === 0) {
      return {barcodes: normalizedBarcodes, conflicts: [], isAvailable: false};
    }

    const lookups = stores.map((store) => this.findConflictsInStore(store, normalizedBarcodes));
    const results = await Promise.all(lookups);
    const conflicts = results.flat();

    return {
      barcodes: normalizedBarcodes,
      conflicts,
      isAvailable: conflicts.length === 0,
    };
  }

  private async findConflictsInStore(
    store: TStockCodeCheckTargets[number],
    barcodes: string[],
  ): Promise<IStockManagerBarcodeStoreConflict[]> {
    const collectionRef = collection(this.firestore, createSharedPaths().setStoreId(store.storeId).stock());
    const conflicts: IStockManagerBarcodeStoreConflict[] = [];
    const seen = new Set<string>();

    for (const chunk of chunkBarcodes(barcodes)) {
      const [primaryMatches, additionalMatches] = await Promise.all([
        getDocs(query(collectionRef, where(sItemKeyToInt.barcode, 'in', chunk))),
        getDocs(query(collectionRef, where(sItemKeyToInt.multipleBarcodes, 'array-contains-any', chunk))),
      ]);

      this.collectConflicts(primaryMatches, store, chunk, seen, conflicts);
      this.collectConflicts(additionalMatches, store, chunk, seen, conflicts);
    }

    return conflicts;
  }

  private collectConflicts(
    snapshot: QuerySnapshot,
    store: TStockCodeCheckTargets[number],
    chunk: string[],
    seen: Set<string>,
    conflicts: IStockManagerBarcodeStoreConflict[],
  ): void {
    snapshot.docs.forEach((documentSnapshot) => {
      const data = documentSnapshot.data() as Record<string, unknown>;
      const primaryBarcode = normalizeBarcodeValue(data[sItemKeyToInt.barcode] as string | number);
      const additionalBarcodes = normalizeMultipleBarcodesValue(data[sItemKeyToInt.multipleBarcodes]);
      const existingStockCode = toRawStockCode(documentSnapshot.id);

      [primaryBarcode, ...additionalBarcodes].forEach((barcode: string) => {
        if (!barcode || !chunk.includes(barcode)) {
          return;
        }
        const key = `${store.storeId}|${barcode}|${existingStockCode}`;
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        conflicts.push({
          barcode,
          existingStockCode,
          existingStockDescription: this.readDescription(data),
          isPrimaryBarcode: barcode === primaryBarcode,
          storeId: store.storeId,
          storeName: store.name ?? store.storeId,
        });
      });
    });
  }

  private readDescription(data: Record<string, unknown>): string {
    const description = data[sItemKeyToInt.desc];
    return typeof description === 'string' && description.trim() ? description.trim() : 'No description';
  }

}
